import { faBroom, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { GTable, IGColumnProps } from '../../../__legacy__/components/table/GTable';
import { S_PaginationProps } from '../../../design/pagination/S_Pagination';
import { RowLayout } from '../../common/layouts/rowLayout/RowLayout';
import { IWorkspace } from '../admin.store';

interface IProps {
  workspaces: IWorkspace[];
  isLoading: boolean;
  sorting: {
    orderBy: string;
    orderType: string;
  };
  pagination: S_PaginationProps;
  onSortingChange: (orderBy: string, orderType: string) => Promise<void>;
  onChangeWorkspaceCostLimit(workspace: IWorkspace): void;
  onResetWorkspace(workspace: IWorkspace): void;
}

export const AdminWorkspacesList = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const renderName = (workspace: IWorkspace): ReactElement => <span>{workspace.name}</span>;
  const renderRealCosts = (workspace: IWorkspace): ReactElement => (
    <span>{workspace.totalCostActual === null ? t('na') : `$${workspace.totalCostActual}`}</span>
  );
  const renderCalculatedCosts = (workspace: IWorkspace): ReactElement => (
    <span>{workspace.totalCostCalculated === null ? t('na') : `$${workspace.totalCostCalculated}`}</span>
  );
  const renderCloudCostsLimit = (workspace: IWorkspace): ReactElement => <span>${workspace.cloudCostsLimit}</span>;

  const renderActions = (workspace: IWorkspace): ReactElement => {
    return (
      <RowLayout size='sm'>
        <IconButton
          size='sm'
          color='primary'
          onClick={(): void => {
            props.onChangeWorkspaceCostLimit(workspace);
          }}
          title={t('edit_cost_limit')}
        >
          <FontAwesomeIcon icon={faEdit} />
        </IconButton>
        <IconButton
          size='sm'
          color='danger'
          onClick={(): void => {
            props.onResetWorkspace(workspace);
          }}
          title={t('reset_workspace')}
        >
          <FontAwesomeIcon icon={faBroom} />
        </IconButton>
      </RowLayout>
    );
  };

  const gColumns: IGColumnProps<IWorkspace>[] = [
    {
      sortBy: 'name',
      headerName: t('workspace_name'),

      renderer: renderName,
    },
    {
      sortBy: 'ownerEmail',
      headerName: t('owner_email'),
      renderer: (x) => x.ownerEmail,
    },
    {
      sortBy: 'ownerCompany',
      headerName: t('owner_company'),
      renderer: (x) => x.ownerCompany,
    },
    {
      sortBy: 'cloudCostsLimit',
      headerName: t('cloud_costs_limit'),
      renderer: renderCloudCostsLimit,
    },
    {
      headerName: t('real_costs'),

      renderer: renderRealCosts,
    },
    {
      headerName: t('calculated_costs'),

      renderer: renderCalculatedCosts,
    },
    {
      headerName: t('actions'),
      actions: true,
      renderer: renderActions,
    },
  ];

  return (
    <GTable
      isLoading={props.isLoading}
      columns={gColumns}
      items={props.workspaces}
      paginationProps={props.pagination}
      onSortChanged={props.onSortingChange}
      sortingModel={[props.sorting]}
      noItemsPlaceholder={t('no_workspaces_have_been_found')}
    />
  );
});
